import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import Session from "users/__session/session";
import { ProposalContext } from "work/proposals/__constants/constants";
import ProposalSchema, { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import WorkAgreementQuery from "work/work-agreements/_api/queries/work-agreement-query";
import BarebonesWorkAgreementResponse from "work/work-agreements/_api/responses/barebones-work-agreement-response";
import { WorkAgreementJSON, WorkAgreementJSONZ } from "work/work-agreements/_schemas/work-agreement-schema";
import VendorTeamSchema, { VendorTeamJSON } from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import WorkAgreement from "work/work-agreements/work-agreement";
import z from "zod";

export const workAgreementsApi = api.injectEndpoints({
  endpoints: (build) => ({
    // #region Get
    getWorkAgreements: build.query<Paginated<WorkAgreementJSON>, { query: WorkAgreementQuery, interfaceName: string }>({
      query: ({ query }) => ({
        url: "work",
        params: query?.searchParams,
      }),
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      onQueryStarted: async (_args, { dispatch, queryFulfilled }) => {
        const { data: workAgreementsResponse } = await queryFulfilled;
        const session = Session.loadFromStorage(() => { });
        workAgreementsResponse.data.forEach((workAgreementJSON) => {
          if (!workAgreementJSON.id) return;
          dispatch(
            workAgreementsApi.util.upsertQueryData(
              "getWorkAgreementById",
              { workAgreementId: workAgreementJSON.id, session },
              workAgreementJSON,
            ),
          );
        });
      },
      transformResponse: (response: WorkAgreementJSON[], meta): Paginated<WorkAgreementJSON> => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        return {
          data: response,
          headers: PaginatedResponse.parsePaginationHeader(headers),
        };
      },
      providesTags: (result) =>
        result
          ? [
            // Tag each item
            ...(result?.data?.map((item) => ({
              type: "WorkAgreement" as const,
              id: item.id ?? undefined,
            })) ?? []),
            // Tag the list itself
            { type: "WorkAgreement" as const, id: "LIST" },
          ]
          : [{ type: "WorkAgreement" as const, id: "LIST" }],
    }),
    getWorkAgreementById: build.query<WorkAgreementJSON | null, { workAgreementId: string; session: Session }>({
      query: ({ workAgreementId }) => `work/${workAgreementId}`,
      rawResponseSchema: WorkAgreementJSONZ,
      providesTags: (result, error, request) => [{ type: "WorkAgreement", id: request.workAgreementId }],
    }),
    getWorkAgreementsByIds: build.query<BarebonesWorkAgreementResponse[], { workAgreementIds: string[] }>({
      query: ({ workAgreementIds }) => ({
        url: "work/get-by-ids",
        params: workAgreementIds,
      }),
      providesTags: (result) =>
        result
          ? [
            // Tag each item
            ...(result?.map((item) => ({
              type: "WorkAgreement" as const,
              id: item.id ?? undefined,
            })) ?? []),
            // Tag the list itself
            { type: "WorkAgreement" as const, id: "LIST" },
          ]
          : [{ type: "WorkAgreement" as const, id: "LIST" }],
    }),
    getWorkTeamsByWorkAgreementId: build.query<VendorTeamJSON[], { workAgreementId: string }>({
      query: ({ workAgreementId }) => `work/${workAgreementId}/teams`,
      rawResponseSchema: z.array(VendorTeamSchema.VendorTeamJSONZ)
    }),
    // #endregion

    // #region Counts
    getActiveWorkAgreementsCount: build.query<number, { session: Session }>({
      query: ({ session }) => ({
        url: "work",
        params: new URLSearchParams({
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          pageSize: "0",
        }),
      }),
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      transformResponse: (_, meta): number => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        const params = new URLSearchParams(headers);
        return Number.parseInt(params.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "WorkAgreement" as const, id: "ActiveCount" }],
    }),
    getArchivedWorkAgreementsCount: build.query<number, { session: Session }>({
      query: ({ session }) => ({
        url: "work",
        params: new URLSearchParams({
          isArchived: "true",
          context: session.context?.viewingAsVendor ? ProposalContext.Vendor : ProposalContext.Client,
          pageSize: "0",
        }),
      }),
      rawResponseSchema: z.array(WorkAgreementJSONZ),
      transformResponse: (_, meta): number => {
        const headers = meta?.response?.headers.get("x-pagination") ?? "";
        const params = new URLSearchParams(headers);
        return Number.parseInt(params.get("totalElements") ?? "0", 10);
      },
      providesTags: [{ type: "WorkAgreement" as const, id: "ArchiveCount" }],
    }),
    // #endregion

    // #region End Work Agreement
    endWorkAgreement: build.mutation<WorkAgreementJSON, { workAgreementId: string }>({
      query: ({ workAgreementId }) => ({
        url: `work/${workAgreementId}/end-agreement`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: WorkAgreementJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "WorkAgreement", id: args.workAgreementId },
        { type: "WorkAgreement", id: "LIST" },
        { type: "WorkAgreement", id: "ActiveCount" },
        { type: "WorkAgreement", id: "ArchiveCount" },
      ],
    }),
    // #endregion

    // #region Revise Work Agreement
    reviseWorkAgreement: build.mutation<ProposalJSON, { workAgreement: WorkAgreement; session: Session }>({
      query: ({ workAgreement, session }) => {
        if (!session.user) {
          throw new Error("Cannot revise work agreement without a user.");
        }
        if (!workAgreement.id) {
          throw new Error("Cannot revise work agreement without id.");
        }
        return {
          url: `work/${workAgreement.id?.value}/revise`,
          method: "POST",
          body: workAgreement.id.value
        };
      },
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "Proposal", id: args.workAgreement.id?.value },
        { type: "Proposal", id: "LIST" },
      ],
    }),
    // #endregion

    // #region Quit Team
    quitWorkTeam: build.mutation<ProposalJSON, { workId: string }>({
      query: ({ workId }) => ({
        url: `work/${workId}/teams/quit`,
        method: "POST",
        body: {},
      }),
      rawResponseSchema: ProposalSchema.ProposalJSONZ,
      invalidatesTags: (_result, _error, args) => [
        { type: "WorkAgreement", id: args.workId },
        { type: "WorkAgreement", id: "LIST" },
        { type: "WorkAgreement", id: "ActiveCount" },
        { type: "WorkAgreement", id: "ArchiveCount" },
      ],
    }),
    // #endregion
  }),
});

export const {
  useGetWorkAgreementsQuery,
  useGetWorkAgreementByIdQuery,
  useGetWorkAgreementsByIdsQuery,
  useGetWorkTeamsByWorkAgreementIdQuery,
  useGetActiveWorkAgreementsCountQuery,
  useGetArchivedWorkAgreementsCountQuery,
  useEndWorkAgreementMutation,
  useReviseWorkAgreementMutation,
  useQuitWorkTeamMutation,
} = workAgreementsApi;
